import React from 'react';
import { ShieldAlert } from 'lucide-react';
import { EmptyState } from '@kardume/ui';
import type { MerchantUserDto } from '@kardume/types';
import { AdminSectionId, getInitialSection, isSectionVisible } from './utils/navigation';
import DashboardPage from './pages/DashboardPage';
import PromotionsPage from './pages/PromotionsPage';
import ExperiencesPage from './pages/ExperiencesPage';
import CouponsPage from './pages/CouponsPage';
import CustomersPage from './pages/CustomersPage';
import ReviewsPage from './pages/ReviewsPage';
import CashbackPage from './pages/CashbackPage';
import BillingPage from './pages/BillingPage';
import ReportsPage from './pages/ReportsPage';
import SettingsPage from './pages/SettingsPage';
import ProfilePage from './pages/ProfilePage';
import BusinessHoursPage from './pages/BusinessHoursPage';
import EmployeesPage from './pages/EmployeesPage';
import AccessProfilesPage from './pages/AccessProfilesPage';
import MerchantOnboardingPage from './pages/MerchantOnboardingPage';

interface AdminRoutesProps {
  activeSection: AdminSectionId;
  user: MerchantUserDto;
  onUserUpdated: (user: MerchantUserDto) => void;
  permissions: string[];
  isOwner: boolean;
}

const AccessDenied = () => (
  <EmptyState
    icon={<ShieldAlert size={30} />}
    title="Acesso restrito"
    description="Seu perfil não tem permissão para acessar esta área."
  />
);

export function AdminRoutes({ activeSection, user, onUserUpdated, permissions, isOwner }: AdminRoutesProps) {
  const section = getInitialSection(permissions, activeSection, isOwner);
  if (!section || !isSectionVisible(permissions, section, isOwner)) {
    return <AccessDenied />;
  }

  switch (section) {
    case 'dashboard':
      return <DashboardPage />;
    case 'promotions':
      return <PromotionsPage />;
    case 'experiences':
      return <ExperiencesPage permissions={permissions} isOwner={isOwner} />;
    case 'coupons':
      return <CouponsPage />;
    case 'customers':
      return <CustomersPage />;
    case 'reviews':
      return <ReviewsPage />;
    case 'cashback':
      return <CashbackPage />;
    case 'billing':
      return <BillingPage />;
    case 'reports':
      return <ReportsPage />;
    case 'employees':
      return <EmployeesPage />;
    case 'access-profiles':
      return <AccessProfilesPage />;
    case 'settings':
      return <SettingsPage />;
    case 'profile':
      return <ProfilePage user={user} onUserUpdated={onUserUpdated} />;
    case 'business-hours':
      return <BusinessHoursPage />;
    default:
      return <AccessDenied />;
  }
}

// ---------------------------------------------------------------------------
// Public routes - reachable without a session.
//
// The admin app is section-driven (no react-router): `App` resolves the URL
// once, before the auth gate, so a merchant who does not have an account yet
// can open the self-service onboarding wizard. The core exposes
// `POST /v1/onboarding/merchant` as a public endpoint, so no token is needed.
// ---------------------------------------------------------------------------

export type PublicRouteId = 'onboarding';

/** URL paths that map to a public route (kept in sync with the SPA fallback). */
const PUBLIC_ROUTE_PATHS: Record<string, PublicRouteId> = {
  '/cadastro': 'onboarding',
  '/cadastro-lojista': 'onboarding',
  '/onboarding': 'onboarding',
};

/** Resolve a public route from a pathname, or `null` when it needs a session. */
export function resolvePublicRoute(pathname: string): PublicRouteId | null {
  const normalized = `/${pathname.replace(/^\/+|\/+$/g, '').toLowerCase()}`;
  return PUBLIC_ROUTE_PATHS[normalized] ?? null;
}

/** Render the public (logged-out) screen for a resolved route. */
export function PublicRoute({ route }: { route: PublicRouteId }) {
  switch (route) {
    case 'onboarding':
      return <MerchantOnboardingPage />;
    default:
      return null;
  }
}
